"use client"

import { useEffect, useState } from "react"

// Global flag to track if scripts are loaded across all components
let wistiaScriptsLoaded = false
let wistiaScriptsLoading = false
let loadingPromise: Promise<void> | null = null

type WistiaScriptLink = {
  src: string
  rel: "preconnect" | "preload"
}

export default function useWistiaScripts() {
  const [loaded, setLoaded] = useState(wistiaScriptsLoaded)

  useEffect(() => {
    if (wistiaScriptsLoaded) {
      setLoaded(true)
      return
    }

    if (wistiaScriptsLoading && loadingPromise) {
      // If already loading, wait for the existing promise
      loadingPromise.then(() => setLoaded(true))
      return
    }

    // Start loading scripts
    wistiaScriptsLoading = true

    const loadWistiaScripts = async () => {
      try {
        // Check if Wistia is already available (in case scripts were loaded elsewhere)
        if (typeof window !== "undefined" && (window as any).Wistia) {
          wistiaScriptsLoaded = true
          setLoaded(true)
          return
        }

        const scripts: WistiaScriptLink[] = [
          { src: "https://fast.wistia.com/player.js", rel: "preconnect" },
        ]

        // Load all scripts/links in parallel
        await Promise.all(
          scripts.map(
            ({ src, rel }) =>
              new Promise<void>((resolve, reject) => {
                if (["preconnect", "preload"].includes(rel)) {
                  // Check if link already exists
                  const existingLink = document.querySelector(
                    `link[rel="${rel}"][href="${src}"]`,
                  )
                  if (existingLink) {
                    resolve()
                    return
                  }
                  const link = document.createElement("link")
                  link.rel = rel
                  link.href = src
                  if (rel === "preload") {
                    link.as = "script"
                  }
                  link.onload = () => resolve()
                  link.onerror = () =>
                    reject(new Error(`Failed to load link: ${src}`))
                  document.head.appendChild(link)
                  // For preconnect/preload, resolve immediately (no onload event for preconnect)
                  if (rel === "preconnect") {
                    resolve()
                  }
                } else {
                  // Check if script is already loaded
                  const existingScript = document.querySelector(
                    `script[src="${src}"]`,
                  )
                  if (existingScript) {
                    resolve()
                    return
                  }

                  const script = document.createElement("script")
                  script.src = src
                  script.async = true

                  script.onload = () => resolve()
                  script.onerror = () =>
                    reject(new Error(`Failed to load ${src}`))

                  document.head.appendChild(script)
                }
              }),
          ),
        )

        wistiaScriptsLoaded = true
        wistiaScriptsLoading = false
        setLoaded(true)
      } catch (error) {
        console.error("Failed to load Wistia scripts:", error)
        wistiaScriptsLoading = false
      }
    }

    loadingPromise = loadWistiaScripts()
  }, [])

  return loaded
}
